import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Lock, Mail, Eye, EyeOff, User, Briefcase, Calendar, Smartphone, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { login, register, googleLogin, getAuthConfig, getAndroidApkInfo, ANDROID_APK_URL } from '../api'

export default function Login() {
  const navigate = useNavigate()
  const [mode, setMode] = useState('login')
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    activite: '', date_naissance: '', company_name: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleClientId, setGoogleClientId] = useState('')
  const [apkInfo, setApkInfo] = useState(null)
  const googleBtnRef = useRef(null)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  useEffect(() => {
    if (localStorage.getItem('token')) {
      navigate('/dashboard', { replace: true })
      return
    }
    getAuthConfig()
      .then(res => setGoogleClientId(res.data.google_client_id || ''))
      .catch(() => {})
    getAndroidApkInfo()
      .then(res => setApkInfo(res.data))
      .catch(() => setApkInfo({ available: false }))
  }, [navigate])

  const formatSize = (bytes) => {
    if (!bytes) return ''
    const mb = bytes / (1024 * 1024)
    return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${(bytes / 1024).toFixed(0)} Ko`
  }

  // Charge Google Identity Services quand client_id dispo
  useEffect(() => {
    if (!googleClientId) return
    const handleGoogle = async (response) => {
      setLoading(true)
      try {
        const res = await googleLogin(response.credential)
        localStorage.setItem('token', res.data.access_token)
        localStorage.setItem('user_email', res.data.email)
        localStorage.setItem('is_admin', res.data.is_admin ? '1' : '0')
        toast.success('Connexion Google réussie !')
        navigate('/dashboard', { replace: true })
      } catch {
        // interceptor affiche l'erreur
      }
      setLoading(false)
    }
    const init = () => {
      if (!window.google || !googleBtnRef.current) return
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogle,
      })
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black', size: 'large', width: 340, text: 'continue_with',
      })
    }
    if (window.google) {
      init()
    } else {
      const s = document.createElement('script')
      s.src = 'https://accounts.google.com/gsi/client'
      s.async = true
      s.onload = init
      document.body.appendChild(s)
    }
  }, [googleClientId, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.email || !form.password) return toast.error('Email et mot de passe requis.')
    if (mode === 'register' && (!form.first_name || !form.last_name)) {
      return toast.error('Prénom et nom requis.')
    }
    setLoading(true)
    try {
      const res = mode === 'login'
        ? await login({ email: form.email, password: form.password })
        : await register(form)
      localStorage.setItem('token', res.data.access_token)
      localStorage.setItem('user_email', res.data.email)
      localStorage.setItem('is_admin', res.data.is_admin ? '1' : '0')
      toast.success(mode === 'login' ? 'Connexion réussie !' : 'Compte créé !')
      navigate('/dashboard', { replace: true })
    } catch {
      // interceptor
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-600/20 border border-primary-500/30 mb-4">
            <Lock size={28} className="text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-dark-50">ProspectFlow</h1>
          <p className="text-dark-400 mt-1 text-sm">
            {mode === 'login' ? 'Connectez-vous à votre espace' : 'Créez votre compte'}
          </p>
        </div>

        <div className="card">
          {/* Google */}
          {googleClientId && (
            <div className="mb-4">
              <div ref={googleBtnRef} className="flex justify-center" />
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-dark-700" />
                <span className="text-dark-500 text-xs">ou</span>
                <div className="flex-1 h-px bg-dark-700" />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'register' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Prénom *</label>
                    <input className="input-field" value={form.first_name}
                      onChange={e => set('first_name', e.target.value)} placeholder="Jean" />
                  </div>
                  <div>
                    <label className="label">Nom *</label>
                    <input className="input-field" value={form.last_name}
                      onChange={e => set('last_name', e.target.value)} placeholder="Dupont" />
                  </div>
                </div>
                <div>
                  <label className="label flex items-center gap-1"><Briefcase size={12} /> Activité</label>
                  <input className="input-field" value={form.activite}
                    onChange={e => set('activite', e.target.value)} placeholder="Technico-commercial sécurité" />
                </div>
                <div>
                  <label className="label flex items-center gap-1"><Calendar size={12} /> Date de naissance</label>
                  <input type="date" className="input-field" value={form.date_naissance}
                    onChange={e => set('date_naissance', e.target.value)} />
                </div>
              </>
            )}

            <div>
              <label className="label">Adresse email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500" />
                <input type="email" className="input-field pl-9" placeholder="vous@exemple.com"
                  value={form.email} onChange={e => set('email', e.target.value)} autoComplete="email" />
              </div>
            </div>

            <div>
              <label className="label">Mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500" />
                <input type={showPassword ? 'text' : 'password'} className="input-field pl-9 pr-10"
                  placeholder={mode === 'register' ? 'Minimum 6 caractères' : '••••••••'}
                  value={form.password} onChange={e => set('password', e.target.value)}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-500 hover:text-dark-300">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn-primary w-full justify-center py-2.5 mt-2" disabled={loading}>
              {loading ? <Loader2 size={18} className="animate-spin" />
                : mode === 'login' ? 'Se connecter' : 'Créer mon compte'}
            </button>
          </form>

          <p className="text-center text-dark-500 text-sm mt-4">
            {mode === 'login'
              ? <>Pas encore de compte ?{' '}
                  <button onClick={() => setMode('register')} className="text-primary-400 hover:text-primary-300 font-medium">
                    Créer un compte
                  </button></>
              : <>Déjà un compte ?{' '}
                  <button onClick={() => setMode('login')} className="text-primary-400 hover:text-primary-300 font-medium">
                    Se connecter
                  </button></>}
          </p>

          <p className="text-center text-dark-600 text-xs mt-4">
            <a href="/legal" className="hover:text-dark-400">Conditions d'utilisation</a>
            {' · '}
            <a href="/legal" className="hover:text-dark-400">Politique de confidentialité</a>
          </p>
        </div>

        {apkInfo?.available && (
          <a
            href={ANDROID_APK_URL}
            className="mt-4 flex items-center justify-between gap-3 p-4 rounded-xl bg-dark-900 border border-dark-700 hover:border-primary-500/50 hover:bg-dark-800 transition group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-600/20 border border-primary-500/30 flex items-center justify-center">
                <Smartphone size={20} className="text-primary-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-dark-100">App Android</div>
                <div className="text-xs text-dark-500">
                  Télécharger l'APK
                  {apkInfo.size ? ` · ${formatSize(apkInfo.size)}` : ''}
                </div>
              </div>
            </div>
            <Download size={18} className="text-dark-500 group-hover:text-primary-400" />
          </a>
        )}
      </div>
    </div>
  )
}
